import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, ADMIN_PATH } from '../services/adminApi';
import './AdminDashboard.css';

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'analytics' | 'pins' | 'moderation' | 'ips' | 'matching' | 'errors';

interface Stats {
  totalPins: number;
  uniqueIps: number;
  dauToday: number;
  mau: number;
  flaggedPins: number;
  blockedIps: number;
  totalSearches: number;
  totalListings: number;
  top5Cities: { city: string; count: number }[];
  topRents: { city: string; maxRent: number }[];
  eventBreakdown: Record<string, number>;
  formStats: Record<string, { opens: number; submits: number; abandons: number }>;
  themeCounts: { light: number; dark: number };
  dau: { date: string; count: number }[];
  devices: { mobile: number; tablet: number; desktop: number };
}

interface FlaggedPin {
  id: string;
  address: string;
  city: string;
  bedrooms: number;
  monthly_rent: number;
  reports: number;
  created_at: string;
  recentReports: { reason: string; created_at: string }[];
}

interface IpEntry {
  ip: string;
  requestCount: number;
  lastSeen: string;
  firstSeen: string;
  device: string;
  isBlocked: boolean;
  topEvents: { type: string; count: number }[];
}

interface BlockedIp {
  id: string;
  ip_address: string;
  reason: string;
  blocked_at: string;
  expires_at: string | null;
}

interface MatchPerson {
  id: string;
  type: string;
  city: string;
  address: string | null;
  budget: number;
  budget_per_room: number | null;
  gender: string;
  created_at: string;
  // seeker-specific
  bhk_preference?: number | null;
  user_gender?: string | null;
  move_in_timeline?: string | null;
  food_preference?: string | null;
  you_are?: string | null;
  comfortable_with?: string | null;
  employment_status?: string | null;
  smoking_habits?: string | null;
  bath_type?: string | null;
  furnishing?: string | null;
  amenities?: string[];
  utilities_included?: string[];
  pets_allowed_status?: string | null;
  transit_distance?: string | null;
  school_rating?: number | null;
  description?: string | null;
  lifestyle?: string | null;
  email?: string;
  phone?: string | null;
  property_type?: string | null;
  // poster-specific
  bhk?: number | null;
  baths?: string | null;
  rental_timeline?: string | null;
  move_in_date?: string | null;
  availability?: string | null;
  includes_maintenance?: boolean | null;
}

interface RoomMatch {
  seeker: MatchPerson;
  poster: MatchPerson;
  distanceKm: number | null;
  isPotential: boolean;
}

interface SeekerRow {
  id: string;
  search_type: string;
  property_type: string;
  budget: number;
  bhk_preference: string | null;
  move_in_timeline: string | null;
  food_preference: string | null;
  you_are: string | null;
  comfortable_with: string | null;
  employment_status: string | null;
  smoking_habits: string | null;
  bath_type: string | null;
  furnishing: string | null;
  amenities: string[];
  utilities_included: string[];
  pets_allowed_status: string | null;
  transit_distance: string | null;
  school_rating: number | null;
  description: string | null;
  lifestyle: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  city: string;
  created_at: string;
}

interface PosterRow {
  id: string;
  listing_type: string;
  property_type: string;
  budget: number;
  bhk: number | null;
  baths: string | null;
  availability: string | null;
  desired_tenant_gender: string;
  furnishing: string | null;
  amenities: string[];
  utilities_included: string[];
  pets_allowed_status: string | null;
  transit_distance: string | null;
  school_rating: number | null;
  description: string | null;
  email: string;
  phone: string | null;
  address: string;
  city: string;
  created_at: string;
}

interface ErrorEntry {
  id: string;
  error_type: string;
  error_message: string;
  request_path: string;
  ip_address: string;
  created_at: string;
  details: Record<string, unknown> | null;
}

// ── Mini helpers ──────────────────────────────────────────────────────────────

function fmt(n: number) { return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n); }
function fmtDate(s: string) { return new Date(s).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
function fmtCurrency(n: number) { return `$${n.toLocaleString()}`; }

function BarChart({ data, colorVar = '#E85D3C' }: { data: { label: string; value: number }[]; colorVar?: string }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="bar-chart">
      {data.map((d) => (
        <div key={d.label} className="bar-row">
          <span className="bar-label">{d.label}</span>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${(d.value / max) * 100}%`, background: colorVar }} />
          </div>
          <span className="bar-val">{fmt(d.value)}</span>
        </div>
      ))}
    </div>
  );
}

function DauChart({ dau }: { dau: { date: string; count: number }[] }) {
  const sorted = [...dau].sort((a, b) => a.date.localeCompare(b.date)).slice(-14);
  const max = Math.max(...sorted.map((d) => d.count), 1);
  return (
    <div className="dau-chart">
      {sorted.map((d) => (
        <div key={d.date} className="dau-col" title={`${d.date}: ${d.count} visitors`}>
          <div className="dau-bar" style={{ height: `${Math.max((d.count / max) * 60, 2)}px` }} />
          <span className="dau-label">{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub, accent }: { icon: string; label: string; value: string | number; sub?: string; accent?: boolean }) {
  return (
    <div className={`stat-card${accent ? ' stat-card--accent' : ''}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
        {sub && <div className="stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

// ── Auth guard ────────────────────────────────────────────────────────────────

function useAdminGuard() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate(`/${ADMIN_PATH}`, { replace: true });
    }
  }, [navigate]);
}

function logout(navigate: ReturnType<typeof useNavigate>) {
  localStorage.removeItem('admin_token');
  navigate(`/${ADMIN_PATH}`, { replace: true });
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS: { key: Tab; icon: string; label: string }[] = [
  { key: 'overview',   icon: '◈', label: 'Overview' },
  { key: 'analytics',  icon: '⟢', label: 'Analytics' },
  { key: 'pins',       icon: '📍', label: 'All Pins' },
  { key: 'moderation', icon: '⚑', label: 'Moderation' },
  { key: 'ips',        icon: '⬡', label: 'IP Manager' },
  { key: 'matching',   icon: '⇌', label: 'Room Matching' },
  { key: 'errors',     icon: '⚠', label: 'Error Log' },
];

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab({ stats }: { stats: Stats | null }) {
  if (!stats) return <div className="ad-loading">Loading stats…</div>;

  const totalTheme = stats.themeCounts.light + stats.themeCounts.dark;
  const darkPct = totalTheme ? Math.round((stats.themeCounts.dark / totalTheme) * 100) : 0;

  return (
    <div className="tab-content">
      <div className="stats-grid">
        <StatCard icon="📍" label="Total Pins" value={fmt(stats.totalPins)} accent />
        <StatCard icon="🌐" label="Unique Visitors" value={fmt(stats.uniqueIps)} />
        <StatCard icon="📅" label="Active Today" value={stats.dauToday} sub="unique IPs" />
        <StatCard icon="📈" label="MAU (30d)" value={fmt(stats.mau)} />
        <StatCard icon="🚩" label="Flagged Pins" value={stats.flaggedPins} accent={stats.flaggedPins > 0} />
        <StatCard icon="🔒" label="Blocked IPs" value={stats.blockedIps} />
        <StatCard icon="🔍" label="Room Seekers" value={stats.totalSearches} />
        <StatCard icon="🏠" label="Room Posters" value={stats.totalListings} />
      </div>

      <div className="panels-row">
        <div className="panel">
          <h3 className="panel-title">Top 5 Cities by Pins</h3>
          <BarChart
            data={stats.top5Cities.map((c) => ({ label: c.city, value: c.count }))}
            colorVar="#E85D3C"
          />
        </div>

        <div className="panel">
          <h3 className="panel-title">Highest Rent by City</h3>
          <BarChart
            data={stats.topRents.slice(0, 8).map((r) => ({ label: r.city, value: r.maxRent }))}
            colorVar="#1A2744"
          />
          {stats.topRents[0] && (
            <p className="panel-note">Peak: {fmtCurrency(stats.topRents[0].maxRent)}/mo in {stats.topRents[0].city}</p>
          )}
        </div>
      </div>

      <div className="panels-row">
        <div className="panel">
          <h3 className="panel-title">Daily Unique Visitors (14d)</h3>
          <DauChart dau={stats.dau} />
        </div>

        <div className="panel">
          <h3 className="panel-title">Device Types</h3>
          <div className="device-donut">
            {(['mobile', 'tablet', 'desktop'] as const).map((type) => {
              const total = stats.devices.mobile + stats.devices.tablet + stats.devices.desktop;
              const pct = total ? Math.round((stats.devices[type] / total) * 100) : 0;
              const colors: Record<string, string> = { mobile: '#E85D3C', tablet: '#8FA987', desktop: '#1A2744' };
              return (
                <div key={type} className="device-row">
                  <div className="device-dot" style={{ background: colors[type] }} />
                  <span className="device-type">{type}</span>
                  <div className="device-bar">
                    <div className="device-fill" style={{ width: `${pct}%`, background: colors[type] }} />
                  </div>
                  <span className="device-pct">{pct}%</span>
                </div>
              );
            })}
          </div>

          <h3 className="panel-title" style={{ marginTop: 20 }}>Theme Preference</h3>
          <div className="theme-split">
            <div className="theme-bar">
              <div className="theme-light" style={{ width: `${100 - darkPct}%` }} title={`Light: ${100 - darkPct}%`} />
              <div className="theme-dark" style={{ width: `${darkPct}%` }} title={`Dark: ${darkPct}%`} />
            </div>
            <div className="theme-legend">
              <span>☀ Light {100 - darkPct}%</span>
              <span>🌙 Dark {darkPct}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────────

function AnalyticsTab({ stats }: { stats: Stats | null }) {
  if (!stats) return <div className="ad-loading">Loading analytics…</div>;

  const formNames: Record<string, string> = {
    pin_rent: 'Pin Rental',
    post_room: 'Post Room',
    post_property: 'Post Unit',
    seek_room: 'Seek Room',
    seek_property: 'Seek Unit',
  };

  const eventOrder = [
    'page_view', 'near_me_click', 'city_search', 'sidebar_open', 'guide_open',
    'share_click', 'theme_change', 'pin_click', 'api_call',
  ];
  const sortedEvents = [
    ...eventOrder.filter((e) => stats.eventBreakdown[e] !== undefined).map((e) => ({ label: e, value: stats.eventBreakdown[e] })),
    ...Object.entries(stats.eventBreakdown)
      .filter(([k]) => !eventOrder.includes(k))
      .sort((a, b) => b[1] - a[1])
      .map(([label, value]) => ({ label, value })),
  ];

  return (
    <div className="tab-content">
      <div className="panels-row">
        <div className="panel panel--wide">
          <h3 className="panel-title">Event Breakdown</h3>
          <BarChart data={sortedEvents.slice(0, 12)} colorVar="#1A2744" />
        </div>
      </div>

      <div className="panels-row">
        <div className="panel">
          <h3 className="panel-title">Form Funnel (Opens → Submits)</h3>
          {Object.keys(stats.formStats).length === 0 ? (
            <p className="panel-empty">No form data yet</p>
          ) : (
            <table className="ad-table">
              <thead>
                <tr>
                  <th>Form</th>
                  <th>Opens</th>
                  <th>Submits</th>
                  <th>Abandons</th>
                  <th>Conv %</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.formStats).map(([form, s]) => {
                  const conv = s.opens ? Math.round((s.submits / s.opens) * 100) : 0;
                  return (
                    <tr key={form}>
                      <td>{formNames[form] || form}</td>
                      <td>{s.opens}</td>
                      <td>{s.submits}</td>
                      <td>{s.abandons}</td>
                      <td>
                        <span className={`badge ${conv >= 50 ? 'badge--green' : conv >= 25 ? 'badge--yellow' : 'badge--red'}`}>
                          {conv}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="panel">
          <h3 className="panel-title">Quick Signals</h3>
          <div className="signal-list">
            {[
              { icon: '👁', label: 'Page Views', value: stats.eventBreakdown['page_view'] || 0 },
              { icon: '📍', label: 'Near Me Clicks', value: stats.eventBreakdown['near_me_click'] || 0 },
              { icon: '🔍', label: 'City Searches', value: stats.eventBreakdown['city_search'] || 0 },
              { icon: '📖', label: 'Guide Opens', value: stats.eventBreakdown['guide_open'] || 0 },
              { icon: '↗', label: 'Share Clicks', value: stats.eventBreakdown['share_click'] || 0 },
              { icon: '🌙', label: 'Dark Mode Switches', value: stats.themeCounts.dark },
              { icon: '☀', label: 'Light Mode Switches', value: stats.themeCounts.light },
              { icon: '🗺', label: 'Pin Clicks', value: stats.eventBreakdown['pin_click'] || 0 },
            ].map((s) => (
              <div key={s.label} className="signal-row">
                <span className="signal-icon">{s.icon}</span>
                <span className="signal-label">{s.label}</span>
                <span className="signal-value">{fmt(s.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── All Pins Tab ──────────────────────────────────────────────────────────────

const PROP_TYPES = ['Basement', 'Apartment', 'Condo', 'Townhouse', 'Semi-Detached', 'Detached'];
const FURNISH_OPTS = ['Furnished', 'Semi-Furnished', 'Unfurnished'];

interface PinRow {
  id: string;
  address: string;
  city: string;
  bedrooms: number | null;
  baths: string | null;
  monthly_rent: number;
  property_type: string | null;
  furnishing: string | null;
  residents_type: string | null;
  pets_allowed_status: string | null;
  square_footage: number | null;
  utilities_included: string[];
  amenities: string[];
  description: string | null;
  email: string | null;
  phone: string | null;
  anonymous_submission: boolean;
  reports: number;
  flagged_for_removal: boolean;
  created_at: string;
}

function PinsTab() {
  const [pins, setPins]           = useState<PinRow[]>([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(0);
  const [expanded, setExpanded]   = useState<string | null>(null);

  // filters
  const [city, setCity]               = useState('');
  const [bedrooms, setBedrooms]       = useState('');
  const [minRent, setMinRent]         = useState('');
  const [maxRent, setMaxRent]         = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [furnishing, setFurnishing]   = useState('');
  const [flagged, setFlagged]         = useState('');
  const [sort, setSort]               = useState('newest');

  const PAGE_SIZE = 50;

  const load = useCallback(async (pg: number) => {
    setLoading(true);
    try {
      const { data: res } = await adminApi.getAllPins({
        city: city || undefined,
        bedrooms: bedrooms ? parseInt(bedrooms) : undefined,
        minRent: minRent ? parseInt(minRent) : undefined,
        maxRent: maxRent ? parseInt(maxRent) : undefined,
        propertyType: propertyType || undefined,
        furnishing: furnishing || undefined,
        flagged: flagged || undefined,
        skip: pg * PAGE_SIZE,
        limit: PAGE_SIZE,
        sort,
      });
      setPins(res.data || []);
      setTotal(res.total || 0);
    } catch { /* interceptor */ }
    finally { setLoading(false); }
  }, [city, bedrooms, minRent, maxRent, propertyType, furnishing, flagged, sort]);

  useEffect(() => { setPage(0); }, [city, bedrooms, minRent, maxRent, propertyType, furnishing, flagged, sort]);
  useEffect(() => { load(page); }, [load, page]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Permanently delete this pin?')) return;
    await adminApi.deletePin(id);
    setPins((p) => p.filter((x) => x.id !== id));
    setTotal((t) => t - 1);
  };

  const handleUnflag = async (id: string) => {
    await adminApi.unflagPin(id);
    setPins((p) => p.map((x) => x.id === id ? { ...x, flagged_for_removal: false, reports: 0 } : x));
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="tab-content">
      {/* ── Filters ── */}
      <div className="pins-filters">
        <input
          className="ad-input pins-filter-city"
          placeholder="Filter by city…"
          value={city}
          onChange={(e) => setCity(e.target.value)}
        />
        <select className="ad-select" value={bedrooms} onChange={(e) => setBedrooms(e.target.value)}>
          <option value="">Beds: Any</option>
          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}{n === 5 ? '+' : ''} bed</option>)}
        </select>
        <input
          className="ad-input pins-filter-rent"
          type="number"
          placeholder="Min rent"
          value={minRent}
          onChange={(e) => setMinRent(e.target.value)}
        />
        <input
          className="ad-input pins-filter-rent"
          type="number"
          placeholder="Max rent"
          value={maxRent}
          onChange={(e) => setMaxRent(e.target.value)}
        />
        <select className="ad-select" value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
          <option value="">Type: Any</option>
          {PROP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="ad-select" value={furnishing} onChange={(e) => setFurnishing(e.target.value)}>
          <option value="">Furnishing: Any</option>
          {FURNISH_OPTS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select className="ad-select" value={flagged} onChange={(e) => setFlagged(e.target.value)}>
          <option value="">All pins</option>
          <option value="false">Clean only</option>
          <option value="true">Flagged only</option>
        </select>
        <select className="ad-select" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="newest">Newest first</option>
          <option value="rent_desc">Rent ↓</option>
          <option value="rent_asc">Rent ↑</option>
          <option value="most_reports">Most reported</option>
        </select>
        <button className="ad-btn-outline" onClick={() => load(page)}>↺ Refresh</button>
      </div>

      <div className="tab-header-row" style={{ marginTop: 12 }}>
        <h2 className="tab-title">
          Pins <span className="count-badge">{total.toLocaleString()}</span>
        </h2>
        {totalPages > 1 && (
          <div className="pagination">
            <button className="ad-btn-sm" disabled={page === 0} onClick={() => setPage(0)}>«</button>
            <button className="ad-btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
            <span className="pagination-info">Page {page + 1} / {totalPages}</span>
            <button className="ad-btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
            <button className="ad-btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="ad-loading">Loading pins…</div>
      ) : pins.length === 0 ? (
        <div className="empty-state">No pins match the current filters</div>
      ) : (
        <table className="ad-table ad-table--full">
          <thead>
            <tr>
              <th>Address</th>
              <th>City</th>
              <th>Type</th>
              <th>Beds</th>
              <th>Rent/mo</th>
              <th>Furnishing</th>
              <th>Contact</th>
              <th>Reports</th>
              <th>Status</th>
              <th>Posted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pins.map((pin) => (
              <React.Fragment key={pin.id}>
                <tr
                  className={`mod-row${expanded === pin.id ? ' mod-row--open' : ''}${pin.flagged_for_removal ? ' row--blocked' : ''}`}
                  onClick={() => setExpanded(expanded === pin.id ? null : pin.id)}
                >
                  <td className="addr-cell">{pin.address}</td>
                  <td>{pin.city}</td>
                  <td>{pin.property_type || <span className="sub-text">—</span>}</td>
                  <td>{pin.bedrooms != null ? `${pin.bedrooms}BR` : <span className="sub-text">—</span>}</td>
                  <td className="rent-cell">{fmtCurrency(pin.monthly_rent)}</td>
                  <td>{pin.furnishing || <span className="sub-text">—</span>}</td>
                  <td>
                    {pin.anonymous_submission
                      ? <span className="sub-text">Anon</span>
                      : <span className="contact-cell">{pin.email || '—'}</span>}
                  </td>
                  <td>
                    {pin.reports > 0
                      ? <span className="badge badge--red">{pin.reports}</span>
                      : <span className="sub-text">0</span>}
                  </td>
                  <td>
                    {pin.flagged_for_removal
                      ? <span className="badge badge--red">🚩 Flagged</span>
                      : <span className="badge badge--green">✓ Clean</span>}
                  </td>
                  <td>{fmtDate(pin.created_at)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="action-btns">
                      {pin.flagged_for_removal && (
                        <button className="ad-btn-sm ad-btn-sm--green" onClick={() => handleUnflag(pin.id)}>Approve</button>
                      )}
                      <button className="ad-btn-sm ad-btn-sm--red" onClick={() => handleDelete(pin.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
                {expanded === pin.id && (
                  <tr className="reports-row">
                    <td colSpan={11}>
                      <div className="pin-detail-grid">
                        {pin.baths && <span><strong>Baths:</strong> {pin.baths}</span>}
                        {pin.square_footage && <span><strong>Sqft:</strong> {pin.square_footage}</span>}
                        {pin.residents_type && <span><strong>Residents:</strong> {pin.residents_type}</span>}
                        {pin.pets_allowed_status && <span><strong>Pets:</strong> {pin.pets_allowed_status}</span>}
                        {pin.utilities_included?.length > 0 && <span><strong>Utilities:</strong> {pin.utilities_included.join(', ')}</span>}
                        {pin.amenities?.length > 0 && <span><strong>Amenities:</strong> {pin.amenities.join(', ')}</span>}
                        {!pin.anonymous_submission && pin.phone && <span><strong>Phone:</strong> {pin.phone}</span>}
                        {pin.description && <span className="pin-detail-desc"><strong>Note:</strong> {pin.description}</span>}
                        <span className="pin-detail-id"><strong>ID:</strong> {pin.id}</span>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div className="pagination pagination--bottom">
          <button className="ad-btn-sm" disabled={page === 0} onClick={() => setPage(0)}>«</button>
          <button className="ad-btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
          <span className="pagination-info">Page {page + 1} / {totalPages}</span>
          <button className="ad-btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
          <button className="ad-btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
        </div>
      )}
    </div>
  );
}

// ── Moderation Tab ────────────────────────────────────────────────────────────

function ModerationTab() {
  const [pins, setPins] = useState<FlaggedPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getFlaggedPins();
      setPins(data.data || []);
    } catch { /* handled by interceptor */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removePin = async (id: string) => {
    if (!window.confirm('Permanently delete this pin? This cannot be undone.')) return;
    await adminApi.deletePin(id);
    setPins((p) => p.filter((x) => x.id !== id));
  };

  const unflag = async (id: string) => {
    await adminApi.unflagPin(id);
    setPins((p) => p.filter((x) => x.id !== id));
  };

  if (loading) return <div className="ad-loading">Loading flagged pins…</div>;

  return (
    <div className="tab-content">
      <div className="tab-header-row">
        <h2 className="tab-title">Flagged Pins <span className="count-badge">{pins.length}</span></h2>
        <button className="ad-btn-outline" onClick={load}>↺ Refresh</button>
      </div>

      {pins.length === 0 ? (
        <div className="empty-state">✓ No pins are currently flagged for removal</div>
      ) : (
        <table className="ad-table ad-table--full">
          <thead>
            <tr>
              <th>Address</th>
              <th>City</th>
              <th>Beds</th>
              <th>Rent</th>
              <th>Reports</th>
              <th>Posted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pins.map((pin) => (
              <React.Fragment key={pin.id}>
                <tr
                  className={`mod-row ${expanded === pin.id ? 'mod-row--open' : ''}`}
                  onClick={() => setExpanded(expanded === pin.id ? null : pin.id)}
                >
                  <td className="addr-cell">{pin.address}</td>
                  <td>{pin.city}</td>
                  <td>{pin.bedrooms}BR</td>
                  <td>${pin.monthly_rent.toLocaleString()}/mo</td>
                  <td>
                    <span className="badge badge--red">{pin.reports} reports</span>
                  </td>
                  <td>{fmtDate(pin.created_at)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="action-btns">
                      <button className="ad-btn-sm ad-btn-sm--green" onClick={() => unflag(pin.id)}>Approve</button>
                      <button className="ad-btn-sm ad-btn-sm--red" onClick={() => removePin(pin.id)}>Remove</button>
                    </div>
                  </td>
                </tr>
                {expanded === pin.id && pin.recentReports.length > 0 && (
                  <tr className="reports-row">
                    <td colSpan={7}>
                      <div className="reports-detail">
                        <strong>Recent reports:</strong>
                        {pin.recentReports.map((r, i) => (
                          <div key={i} className="report-item">
                            <span className="report-reason">{r.reason}</span>
                            <span className="report-date">{fmtDate(r.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── IP Manager Tab ────────────────────────────────────────────────────────────

function IpManagerTab() {
  const [view, setView] = useState<'top' | 'blocked'>('top');
  const [topIps, setTopIps] = useState<IpEntry[]>([]);
  const [blockedIps, setBlockedIps] = useState<BlockedIp[]>([]);
  const [activityIp, setActivityIp] = useState<string | null>(null);
  const [activity, setActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [blockForm, setBlockForm] = useState<{ ip: string; reason: string; expires: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [topRes, blockedRes] = await Promise.all([adminApi.getTopIps(), adminApi.getBlockedIps()]);
      setTopIps(topRes.data.data || []);
      setBlockedIps(blockedRes.data.data || []);
    } catch { /* interceptor */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadActivity = async (ip: string) => {
    setActivityIp(ip);
    const { data } = await adminApi.getIpActivity(ip);
    setActivity(data.data || []);
  };

  const doBlock = async () => {
    if (!blockForm) return;
    await adminApi.blockIp(blockForm.ip, blockForm.reason, blockForm.expires || undefined);
    setBlockForm(null);
    load();
  };

  const doUnblock = async (ip: string) => {
    await adminApi.unblockIp(ip);
    setBlockedIps((b) => b.filter((x) => x.ip_address !== ip));
  };

  if (loading) return <div className="ad-loading">Loading IP data…</div>;

  if (activityIp) {
    return (
      <div className="tab-content">
        <div className="tab-header-row">
          <h2 className="tab-title">Activity: <code>{activityIp}</code></h2>
          <button className="ad-btn-outline" onClick={() => setActivityIp(null)}>← Back</button>
        </div>
        <table className="ad-table ad-table--full">
          <thead><tr><th>Event</th><th>Path</th><th>Time</th></tr></thead>
          <tbody>
            {activity.map((a, i) => (
              <tr key={i}>
                <td><span className="event-badge">{a.event_type}</span></td>
                <td className="path-cell">{a.event_path}</td>
                <td>{fmtDate(a.created_at)}</td>
              </tr>
            ))}
            {activity.length === 0 && <tr><td colSpan={3} className="empty-cell">No activity recorded</td></tr>}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="tab-header-row">
        <div className="view-toggle">
          <button className={`vt-btn${view === 'top' ? ' active' : ''}`} onClick={() => setView('top')}>Top 100 IPs</button>
          <button className={`vt-btn${view === 'blocked' ? ' active' : ''}`} onClick={() => setView('blocked')}>
            Blocked <span className="count-badge">{blockedIps.length}</span>
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ad-btn-outline" onClick={() => setBlockForm({ ip: '', reason: '', expires: '' })}>+ Block IP</button>
          <button className="ad-btn-outline" onClick={load}>↺ Refresh</button>
        </div>
      </div>

      {blockForm && (
        <div className="block-form">
          <h4 className="block-form-title">Block an IP Address</h4>
          <div className="block-form-fields">
            <input className="ad-input" placeholder="IP address" value={blockForm.ip} onChange={(e) => setBlockForm({ ...blockForm, ip: e.target.value })} />
            <input className="ad-input" placeholder="Reason" value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} />
            <input className="ad-input" type="datetime-local" placeholder="Expires (optional)" value={blockForm.expires} onChange={(e) => setBlockForm({ ...blockForm, expires: e.target.value })} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ad-btn-sm ad-btn-sm--red" onClick={doBlock} disabled={!blockForm.ip}>Block</button>
              <button className="ad-btn-sm" onClick={() => setBlockForm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {view === 'top' && (
        <table className="ad-table ad-table--full">
          <thead>
            <tr>
              <th>#</th>
              <th>IP Address</th>
              <th>Requests</th>
              <th>Device</th>
              <th>First Seen</th>
              <th>Last Seen</th>
              <th>Top Events</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {topIps.map((ip, i) => (
              <tr key={ip.ip} className={ip.isBlocked ? 'row--blocked' : ''}>
                <td className="rank-cell">{i + 1}</td>
                <td><code className="ip-code">{ip.ip}</code></td>
                <td><span className="badge">{ip.requestCount}</span></td>
                <td><span className="device-badge">{ip.device}</span></td>
                <td>{fmtDate(ip.firstSeen)}</td>
                <td>{fmtDate(ip.lastSeen)}</td>
                <td>
                  <div className="event-tags">
                    {ip.topEvents.map((e) => <span key={e.type} className="event-tag">{e.type} ×{e.count}</span>)}
                  </div>
                </td>
                <td>
                  <div className="action-btns">
                    <button className="ad-btn-sm" onClick={() => loadActivity(ip.ip)}>Activity</button>
                    {ip.isBlocked ? (
                      <button className="ad-btn-sm ad-btn-sm--green" onClick={() => doUnblock(ip.ip)}>Unblock</button>
                    ) : (
                      <button className="ad-btn-sm ad-btn-sm--red" onClick={() => setBlockForm({ ip: ip.ip, reason: 'High volume', expires: '' })}>Block</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {view === 'blocked' && (
        <table className="ad-table ad-table--full">
          <thead>
            <tr><th>IP Address</th><th>Reason</th><th>Blocked At</th><th>Expires</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {blockedIps.map((b) => (
              <tr key={b.id}>
                <td><code className="ip-code">{b.ip_address}</code></td>
                <td>{b.reason}</td>
                <td>{fmtDate(b.blocked_at)}</td>
                <td>{b.expires_at ? fmtDate(b.expires_at) : <span className="badge badge--red">Permanent</span>}</td>
                <td>
                  <button className="ad-btn-sm ad-btn-sm--green" onClick={() => doUnblock(b.ip_address)}>Unblock</button>
                </td>
              </tr>
            ))}
            {blockedIps.length === 0 && <tr><td colSpan={5} className="empty-cell">No blocked IPs</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Room Matching Tab ─────────────────────────────────────────────────────────

function MatchingTab() {
  const [view, setView] = useState<'matches' | 'seekers' | 'posters'>('matches');

  // ── Matches state ──
  const [matchData, setMatchData] = useState<{ matches: RoomMatch[]; totalSeekers: number; totalPosters: number; potentialMatches: number } | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchFilter, setMatchFilter] = useState<'all' | 'potential'>('potential');
  const [expandedMatch, setExpandedMatch] = useState<number | null>(null);

  // ── Seekers state ──
  const [seekers, setSeekers]           = useState<SeekerRow[]>([]);
  const [seekerTotal, setSeekerTotal]   = useState(0);
  const [seekerLoading, setSeekerLoading] = useState(false);
  const [seekerPage, setSeekerPage]     = useState(0);
  const [seekerCity, setSeekerCity]     = useState('');
  const [seekerType, setSeekerType]     = useState('');
  const [seekerExpanded, setSeekerExpanded] = useState<string | null>(null);

  // ── Posters state ──
  const [posters, setPosters]           = useState<PosterRow[]>([]);
  const [posterTotal, setPosterTotal]   = useState(0);
  const [posterLoading, setPosterLoading] = useState(false);
  const [posterPage, setPosterPage]     = useState(0);
  const [posterCity, setPosterCity]     = useState('');
  const [posterType, setPosterType]     = useState('');
  const [posterExpanded, setPosterExpanded] = useState<string | null>(null);

  const PAGE_SIZE = 50;

  const loadMatches = useCallback(async () => {
    setMatchLoading(true);
    try {
      const { data: res } = await adminApi.getRoomMatches();
      setMatchData(res.data);
    } catch { /* interceptor */ } finally { setMatchLoading(false); }
  }, []);

  const loadSeekers = useCallback(async (pg: number) => {
    setSeekerLoading(true);
    try {
      const { data: res } = await adminApi.getAllSeekers({ city: seekerCity || undefined, searchType: seekerType || undefined, skip: pg * PAGE_SIZE, limit: PAGE_SIZE });
      setSeekers(res.data || []);
      setSeekerTotal(res.total || 0);
    } catch { /* interceptor */ } finally { setSeekerLoading(false); }
  }, [seekerCity, seekerType]);

  const loadPosters = useCallback(async (pg: number) => {
    setPosterLoading(true);
    try {
      const { data: res } = await adminApi.getAllPosters({ city: posterCity || undefined, listingType: posterType || undefined, skip: pg * PAGE_SIZE, limit: PAGE_SIZE });
      setPosters(res.data || []);
      setPosterTotal(res.total || 0);
    } catch { /* interceptor */ } finally { setPosterLoading(false); }
  }, [posterCity, posterType]);

  useEffect(() => { if (view === 'matches' && !matchData) loadMatches(); }, [view, matchData, loadMatches]);
  useEffect(() => { setSeekerPage(0); }, [seekerCity, seekerType]);
  useEffect(() => { if (view === 'seekers') loadSeekers(seekerPage); }, [view, loadSeekers, seekerPage]);
  useEffect(() => { setPosterPage(0); }, [posterCity, posterType]);
  useEffect(() => { if (view === 'posters') loadPosters(posterPage); }, [view, loadPosters, posterPage]);

  const seekerPages = Math.ceil(seekerTotal / PAGE_SIZE);
  const posterPages = Math.ceil(posterTotal / PAGE_SIZE);

  const typeLabel = (t: string) => {
    const map: Record<string, string> = { LookingForRoom: 'Seek Room', LookingForWhole: 'Seek Unit', RentingRoom: 'Post Room', RentingWhole: 'Post Unit' };
    return map[t] || t;
  };

  const shownMatches = matchData
    ? (matchFilter === 'potential' ? matchData.matches.filter((m) => m.isPotential) : matchData.matches)
    : [];

  return (
    <div className="tab-content">

      {/* ── Outer tab switcher ── */}
      <div className="tab-header-row">
        <div className="view-toggle">
          <button className={`vt-btn${view === 'matches' ? ' active' : ''}`} onClick={() => setView('matches')}>⇌ Matches</button>
          <button className={`vt-btn${view === 'seekers' ? ' active' : ''}`} onClick={() => setView('seekers')}>
            🔍 Seekers {seekerTotal > 0 && <span className="count-badge">{seekerTotal}</span>}
          </button>
          <button className={`vt-btn${view === 'posters' ? ' active' : ''}`} onClick={() => setView('posters')}>
            🏠 Posters {posterTotal > 0 && <span className="count-badge">{posterTotal}</span>}
          </button>
        </div>
      </div>

      {/* ── Matches view ── */}
      {view === 'matches' && (
        <>
          {matchLoading && <div className="ad-loading">Computing matches…</div>}
          {!matchLoading && !matchData && <div className="empty-state">Failed to load matching data</div>}
          {!matchLoading && matchData && (
            <>
              <div className="stats-grid stats-grid--sm" style={{ marginTop: 16 }}>
                <StatCard icon="🔍" label="Total Seekers" value={matchData.totalSeekers} />
                <StatCard icon="🏠" label="Total Posters" value={matchData.totalPosters} />
                <StatCard icon="✓" label="Potential Matches (≤10km)" value={matchData.potentialMatches} accent />
                <StatCard icon="⇌" label="All Compatible" value={matchData.matches.length} />
              </div>
              <div className="tab-header-row" style={{ marginTop: 16 }}>
                <div className="view-toggle">
                  <button className={`vt-btn${matchFilter === 'potential' ? ' active' : ''}`} onClick={() => setMatchFilter('potential')}>
                    Potential (≤10km) <span className="count-badge">{matchData.potentialMatches}</span>
                  </button>
                  <button className={`vt-btn${matchFilter === 'all' ? ' active' : ''}`} onClick={() => setMatchFilter('all')}>
                    All Compatible <span className="count-badge">{matchData.matches.length}</span>
                  </button>
                </div>
                <button className="ad-btn-outline" onClick={loadMatches}>↺ Refresh</button>
              </div>
              <table className="ad-table ad-table--full">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Seeker</th>
                    <th>Seeker Budget</th>
                    <th>Poster</th>
                    <th>Poster Ask</th>
                    <th>Distance</th>
                    <th>City</th>
                  </tr>
                </thead>
                <tbody>
                  {shownMatches.map((m, i) => (
                    <React.Fragment key={i}>
                      <tr
                        className={`mod-row${expandedMatch === i ? ' mod-row--open' : ''}`}
                        onClick={() => setExpandedMatch(expandedMatch === i ? null : i)}
                      >
                        <td>{m.isPotential ? <span className="badge badge--green">✓ Potential</span> : <span className="badge badge--yellow">Near</span>}</td>
                        <td>
                          <div>{typeLabel(m.seeker.type)}</div>
                          <div className="sub-text">{m.seeker.you_are || m.seeker.user_gender || 'Gender pref: ' + m.seeker.gender}</div>
                        </td>
                        <td>${m.seeker.budget.toLocaleString()}/mo</td>
                        <td>
                          <div>{typeLabel(m.poster.type)}</div>
                          <div className="sub-text addr-cell">{m.poster.address}</div>
                        </td>
                        <td>${m.poster.budget.toLocaleString()}/mo</td>
                        <td>{m.distanceKm !== null ? <span className={m.distanceKm <= 10 ? 'dist-near' : 'dist-far'}>{m.distanceKm} km</span> : <span className="sub-text">Same city</span>}</td>
                        <td>{m.seeker.city}</td>
                      </tr>
                      {expandedMatch === i && (
                        <tr className="reports-row">
                          <td colSpan={7}>
                            <div className="match-detail-grid">
                              {/* Seeker column */}
                              <div className="match-detail-col">
                                <div className="match-detail-col-head">
                                  <span className="badge badge--yellow">{typeLabel(m.seeker.type)}</span>
                                  <span className="match-detail-col-title">Seeker</span>
                                </div>
                                {/* Contact card */}
                                <div className="match-contact-card">
                                  {m.seeker.you_are && <div className="match-contact-name">{m.seeker.you_are}{m.seeker.user_gender ? ` · ${m.seeker.user_gender}` : ''}</div>}
                                  {m.seeker.email && (
                                    <a href={`mailto:${m.seeker.email}`} className="match-contact-email">{m.seeker.email}</a>
                                  )}
                                  {m.seeker.phone && (
                                    <a href={`tel:${m.seeker.phone}`} className="match-contact-phone">{m.seeker.phone}</a>
                                  )}
                                  {!m.seeker.phone && <span className="match-contact-no-phone">No phone provided</span>}
                                </div>
                                <div className="pin-detail-grid">
                                  {m.seeker.address && <span><strong>Area:</strong> {m.seeker.address.split(',').slice(0, 2).join(',')}</span>}
                                  <span><strong>City:</strong> {m.seeker.city}</span>
                                  <span><strong>Budget:</strong> {fmtCurrency(m.seeker.budget)}/mo{m.seeker.budget_per_room ? ` · ${fmtCurrency(m.seeker.budget_per_room)}/room` : ''}</span>
                                  {m.seeker.property_type && <span><strong>Property:</strong> {m.seeker.property_type}</span>}
                                  {m.seeker.bhk_preference != null && <span><strong>BHK pref:</strong> {m.seeker.bhk_preference}</span>}
                                  {m.seeker.move_in_timeline && <span><strong>Timeline:</strong> {m.seeker.move_in_timeline}</span>}
                                  {m.seeker.comfortable_with && <span><strong>Comfortable with:</strong> {m.seeker.comfortable_with}</span>}
                                  {m.seeker.employment_status && <span><strong>Employment:</strong> {m.seeker.employment_status}</span>}
                                  {m.seeker.food_preference && <span><strong>Food:</strong> {m.seeker.food_preference}</span>}
                                  {m.seeker.smoking_habits && <span><strong>Smoking:</strong> {m.seeker.smoking_habits}</span>}
                                  {m.seeker.bath_type && <span><strong>Bath:</strong> {m.seeker.bath_type}</span>}
                                  {m.seeker.furnishing && <span><strong>Furnishing:</strong> {m.seeker.furnishing}</span>}
                                  {m.seeker.pets_allowed_status && m.seeker.pets_allowed_status !== 'No' && <span><strong>Pets:</strong> {m.seeker.pets_allowed_status}</span>}
                                  {m.seeker.transit_distance && <span><strong>Transit:</strong> {m.seeker.transit_distance}</span>}
                                  {m.seeker.school_rating ? <span><strong>School:</strong> {m.seeker.school_rating}/10</span> : null}
                                  {m.seeker.amenities && m.seeker.amenities.length > 0 && <span><strong>Amenities:</strong> {m.seeker.amenities.join(', ')}</span>}
                                  {m.seeker.utilities_included && m.seeker.utilities_included.length > 0 && <span><strong>Utilities:</strong> {m.seeker.utilities_included.join(', ')}</span>}
                                  {m.seeker.lifestyle && <span className="pin-detail-desc"><strong>Lifestyle:</strong> {m.seeker.lifestyle}</span>}
                                  {m.seeker.description && <span className="pin-detail-desc"><strong>One-liner:</strong> {m.seeker.description}</span>}
                                  <span className="pin-detail-id"><strong>ID:</strong> {m.seeker.id}</span>
                                </div>
                              </div>

                              {/* Divider */}
                              <div className="match-detail-divider" />

                              {/* Poster column */}
                              <div className="match-detail-col">
                                <div className="match-detail-col-head">
                                  <span className="badge badge--green">{typeLabel(m.poster.type)}</span>
                                  <span className="match-detail-col-title">Poster</span>
                                </div>
                                {/* Contact card */}
                                <div className="match-contact-card">
                                  {m.poster.you_are && <div className="match-contact-name">{m.poster.you_are}{m.poster.gender ? ` · wants ${m.poster.gender}` : ''}</div>}
                                  {m.poster.email && (
                                    <a href={`mailto:${m.poster.email}`} className="match-contact-email">{m.poster.email}</a>
                                  )}
                                  {m.poster.phone && (
                                    <a href={`tel:${m.poster.phone}`} className="match-contact-phone">{m.poster.phone}</a>
                                  )}
                                  {!m.poster.phone && <span className="match-contact-no-phone">No phone provided</span>}
                                </div>
                                <div className="pin-detail-grid">
                                  {m.poster.address && <span><strong>Address:</strong> {m.poster.address}</span>}
                                  <span><strong>City:</strong> {m.poster.city}</span>
                                  <span><strong>Asking:</strong> {fmtCurrency(m.poster.budget)}/mo{m.poster.budget_per_room ? ` · ${fmtCurrency(m.poster.budget_per_room)}/room` : ''}</span>
                                  {m.poster.property_type && <span><strong>Property:</strong> {m.poster.property_type}</span>}
                                  {m.poster.bhk != null && <span><strong>BHK:</strong> {m.poster.bhk}</span>}
                                  {m.poster.baths && <span><strong>Baths:</strong> {m.poster.baths}</span>}
                                  {m.poster.rental_timeline && <span><strong>Timeline:</strong> {m.poster.rental_timeline}</span>}
                                  {m.poster.availability && <span><strong>Availability:</strong> {m.poster.availability}</span>}
                                  {m.poster.move_in_date && <span><strong>Move-in date:</strong> {m.poster.move_in_date}</span>}
                                  {m.poster.includes_maintenance != null && <span><strong>Maintenance incl:</strong> {m.poster.includes_maintenance ? 'Yes' : 'No'}</span>}
                                  {m.poster.food_preference && <span><strong>Food:</strong> {m.poster.food_preference}</span>}
                                  {m.poster.smoking_habits && <span><strong>Smoking:</strong> {m.poster.smoking_habits}</span>}
                                  {m.poster.bath_type && <span><strong>Bath type:</strong> {m.poster.bath_type}</span>}
                                  {m.poster.furnishing && <span><strong>Furnishing:</strong> {m.poster.furnishing}</span>}
                                  {m.poster.pets_allowed_status && m.poster.pets_allowed_status !== 'No' && <span><strong>Pets:</strong> {m.poster.pets_allowed_status}</span>}
                                  {m.poster.transit_distance && <span><strong>Transit:</strong> {m.poster.transit_distance}</span>}
                                  {m.poster.school_rating ? <span><strong>School:</strong> {m.poster.school_rating}/10</span> : null}
                                  {m.poster.amenities && m.poster.amenities.length > 0 && <span><strong>Amenities:</strong> {m.poster.amenities.join(', ')}</span>}
                                  {m.poster.utilities_included && m.poster.utilities_included.length > 0 && <span><strong>Utilities:</strong> {m.poster.utilities_included.join(', ')}</span>}
                                  {m.poster.lifestyle && <span className="pin-detail-desc"><strong>Lifestyle:</strong> {m.poster.lifestyle}</span>}
                                  {m.poster.description && <span className="pin-detail-desc"><strong>Description:</strong> {m.poster.description}</span>}
                                  <span className="pin-detail-id"><strong>ID:</strong> {m.poster.id}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {shownMatches.length === 0 && <tr><td colSpan={7} className="empty-cell">No matches found</td></tr>}
                </tbody>
              </table>
            </>
          )}
        </>
      )}

      {/* ── Seekers view ── */}
      {view === 'seekers' && (
        <>
          <div className="pins-filters" style={{ marginTop: 12 }}>
            <input className="ad-input pins-filter-city" placeholder="Filter by city…" value={seekerCity} onChange={(e) => setSeekerCity(e.target.value)} />
            <select className="ad-select" value={seekerType} onChange={(e) => setSeekerType(e.target.value)}>
              <option value="">All types</option>
              <option value="LookingForRoom">Seek Room</option>
              <option value="LookingForWhole">Seek Unit</option>
            </select>
            <button className="ad-btn-outline" onClick={() => loadSeekers(seekerPage)}>↺ Refresh</button>
          </div>
          <div className="tab-header-row" style={{ marginTop: 12 }}>
            <h2 className="tab-title">Seekers <span className="count-badge">{seekerTotal}</span></h2>
            {seekerPages > 1 && (
              <div className="pagination">
                <button className="ad-btn-sm" disabled={seekerPage === 0} onClick={() => setSeekerPage(0)}>«</button>
                <button className="ad-btn-sm" disabled={seekerPage === 0} onClick={() => setSeekerPage((p) => p - 1)}>‹</button>
                <span className="pagination-info">Page {seekerPage + 1} / {seekerPages}</span>
                <button className="ad-btn-sm" disabled={seekerPage >= seekerPages - 1} onClick={() => setSeekerPage((p) => p + 1)}>›</button>
                <button className="ad-btn-sm" disabled={seekerPage >= seekerPages - 1} onClick={() => setSeekerPage(seekerPages - 1)}>»</button>
              </div>
            )}
          </div>
          {seekerLoading ? <div className="ad-loading">Loading seekers…</div> : (
            <table className="ad-table ad-table--full">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Area</th>
                  <th>City</th>
                  <th>Budget</th>
                  <th>You Are</th>
                  <th>BHK Pref</th>
                  <th>Prop Type</th>
                  <th>Timeline</th>
                  <th>Email</th>
                  <th>Posted</th>
                </tr>
              </thead>
              <tbody>
                {seekers.map((s) => (
                  <React.Fragment key={s.id}>
                    <tr className={`mod-row${seekerExpanded === s.id ? ' mod-row--open' : ''}`} onClick={() => setSeekerExpanded(seekerExpanded === s.id ? null : s.id)}>
                      <td><span className="badge badge--yellow">{typeLabel(s.search_type)}</span></td>
                      <td className="addr-cell">{s.address ? s.address.split(',').slice(0, 2).join(',') : <span className="sub-text">—</span>}</td>
                      <td>{s.city}</td>
                      <td>{fmtCurrency(s.budget)}/mo</td>
                      <td>{s.you_are || <span className="sub-text">—</span>}</td>
                      <td>{s.bhk_preference || <span className="sub-text">Any</span>}</td>
                      <td>{s.property_type || <span className="sub-text">Any</span>}</td>
                      <td>{s.move_in_timeline || <span className="sub-text">—</span>}</td>
                      <td className="contact-cell">{s.email}</td>
                      <td>{fmtDate(s.created_at)}</td>
                    </tr>
                    {seekerExpanded === s.id && (
                      <tr className="reports-row">
                        <td colSpan={10}>
                          <div className="pin-detail-grid">
                            {s.comfortable_with && <span><strong>Comfortable with:</strong> {s.comfortable_with}</span>}
                            {s.employment_status && <span><strong>Employment:</strong> {s.employment_status}</span>}
                            {s.food_preference && <span><strong>Food:</strong> {s.food_preference}</span>}
                            {s.smoking_habits && <span><strong>Smoking:</strong> {s.smoking_habits}</span>}
                            {s.bath_type && <span><strong>Bath:</strong> {s.bath_type}</span>}
                            {s.furnishing && <span><strong>Furnishing:</strong> {s.furnishing}</span>}
                            {s.pets_allowed_status && s.pets_allowed_status !== 'No' && <span><strong>Pets:</strong> {s.pets_allowed_status}</span>}
                            {s.transit_distance && <span><strong>Transit:</strong> {s.transit_distance}</span>}
                            {s.school_rating ? <span><strong>School Rating:</strong> {s.school_rating}/10</span> : null}
                            {s.amenities?.length > 0 && <span><strong>Amenities:</strong> {s.amenities.join(', ')}</span>}
                            {s.utilities_included?.length > 0 && <span><strong>Utilities:</strong> {s.utilities_included.join(', ')}</span>}
                            {s.phone && <span><strong>Phone:</strong> {s.phone}</span>}
                            {s.lifestyle && <span className="pin-detail-desc"><strong>Lifestyle:</strong> {s.lifestyle}</span>}
                            {s.description && <span className="pin-detail-desc"><strong>One-liner:</strong> {s.description}</span>}
                            <span className="pin-detail-id"><strong>ID:</strong> {s.id}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {seekers.length === 0 && <tr><td colSpan={10} className="empty-cell">No seekers found</td></tr>}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* ── Posters view ── */}
      {view === 'posters' && (
        <>
          <div className="pins-filters" style={{ marginTop: 12 }}>
            <input className="ad-input pins-filter-city" placeholder="Filter by city…" value={posterCity} onChange={(e) => setPosterCity(e.target.value)} />
            <select className="ad-select" value={posterType} onChange={(e) => setPosterType(e.target.value)}>
              <option value="">All types</option>
              <option value="RentingRoom">Post Room</option>
              <option value="RentingWhole">Post Unit</option>
            </select>
            <button className="ad-btn-outline" onClick={() => loadPosters(posterPage)}>↺ Refresh</button>
          </div>
          <div className="tab-header-row" style={{ marginTop: 12 }}>
            <h2 className="tab-title">Posters <span className="count-badge">{posterTotal}</span></h2>
            {posterPages > 1 && (
              <div className="pagination">
                <button className="ad-btn-sm" disabled={posterPage === 0} onClick={() => setPosterPage(0)}>«</button>
                <button className="ad-btn-sm" disabled={posterPage === 0} onClick={() => setPosterPage((p) => p - 1)}>‹</button>
                <span className="pagination-info">Page {posterPage + 1} / {posterPages}</span>
                <button className="ad-btn-sm" disabled={posterPage >= posterPages - 1} onClick={() => setPosterPage((p) => p + 1)}>›</button>
                <button className="ad-btn-sm" disabled={posterPage >= posterPages - 1} onClick={() => setPosterPage(posterPages - 1)}>»</button>
              </div>
            )}
          </div>
          {posterLoading ? <div className="ad-loading">Loading posters…</div> : (
            <table className="ad-table ad-table--full">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Address</th>
                  <th>City</th>
                  <th>Budget</th>
                  <th>Bedrooms</th>
                  <th>Baths</th>
                  <th>Prop Type</th>
                  <th>Availability</th>
                  <th>Tenant</th>
                  <th>Email</th>
                  <th>Posted</th>
                </tr>
              </thead>
              <tbody>
                {posters.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr className={`mod-row${posterExpanded === p.id ? ' mod-row--open' : ''}`} onClick={() => setPosterExpanded(posterExpanded === p.id ? null : p.id)}>
                      <td><span className="badge badge--green">{typeLabel(p.listing_type)}</span></td>
                      <td className="addr-cell">{p.address}</td>
                      <td>{p.city}</td>
                      <td>{fmtCurrency(p.budget)}/mo</td>
                      <td>{p.bhk != null ? `${p.bhk}BR` : <span className="sub-text">—</span>}</td>
                      <td>{p.baths || <span className="sub-text">—</span>}</td>
                      <td>{p.property_type || <span className="sub-text">—</span>}</td>
                      <td>{p.availability || <span className="sub-text">—</span>}</td>
                      <td>{p.desired_tenant_gender}</td>
                      <td className="contact-cell">{p.email}</td>
                      <td>{fmtDate(p.created_at)}</td>
                    </tr>
                    {posterExpanded === p.id && (
                      <tr className="reports-row">
                        <td colSpan={11}>
                          <div className="pin-detail-grid">
                            {p.furnishing && <span><strong>Furnishing:</strong> {p.furnishing}</span>}
                            {p.pets_allowed_status && p.pets_allowed_status !== 'No' && <span><strong>Pets:</strong> {p.pets_allowed_status}</span>}
                            {p.transit_distance && <span><strong>Transit:</strong> {p.transit_distance}</span>}
                            {p.school_rating ? <span><strong>School Rating:</strong> {p.school_rating}/10</span> : null}
                            {p.amenities?.length > 0 && <span><strong>Amenities:</strong> {p.amenities.join(', ')}</span>}
                            {p.utilities_included?.length > 0 && <span><strong>Utilities:</strong> {p.utilities_included.join(', ')}</span>}
                            {p.phone && <span><strong>Phone:</strong> {p.phone}</span>}
                            {p.description && <span className="pin-detail-desc"><strong>Description:</strong> {p.description}</span>}
                            <span className="pin-detail-id"><strong>ID:</strong> {p.id}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {posters.length === 0 && <tr><td colSpan={11} className="empty-cell">No posters found</td></tr>}
              </tbody>
            </table>
          )}
        </>
      )}

    </div>
  );
}

// ── Error Log Tab ─────────────────────────────────────────────────────────────

function ErrorLogTab() {
  const [errors, setErrors]       = useState<ErrorEntry[]>([]);
  const [total, setTotal]         = useState(0);
  const [typeCounts, setTypeCounts] = useState<Record<string, number>>({});
  const [loading, setLoading]     = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage]           = useState(0);
  const [expanded, setExpanded]   = useState<string | null>(null);

  const PAGE_SIZE = 100;

  const load = useCallback(async (pg: number) => {
    setLoading(true);
    try {
      const { data } = await adminApi.getErrors(typeFilter || undefined, PAGE_SIZE, pg * PAGE_SIZE);
      setErrors(data.data || []);
      setTotal(data.total || 0);
      setTypeCounts(data.typeCounts || {});
    } catch { /* interceptor */ }
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { setPage(0); }, [typeFilter]);
  useEffect(() => { load(page); }, [load, page]);

  const totalPages   = Math.ceil(total / PAGE_SIZE);
  const grandTotal   = Object.values(typeCounts).reduce((a, b) => a + b, 0);
  const sortedTypes  = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]);

  const badgeColor = (t: string) =>
    t.includes('error') || t.includes('fail') ? 'red' : 'yellow';

  return (
    <div className="tab-content">

      {/* ── Type breakdown chips ── */}
      {!loading && sortedTypes.length > 0 && (
        <div className="error-summary">
          {sortedTypes.map(([type, count]) => (
            <button
              key={type}
              className={`error-type-chip chip--${badgeColor(type)}${typeFilter === type ? ' active' : ''}`}
              onClick={() => setTypeFilter(typeFilter === type ? '' : type)}
              title={`Filter to ${type}`}
            >
              <span className="chip-type">{type}</span>
              <span className="chip-count">{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Header ── */}
      <div className="tab-header-row" style={{ marginTop: 12 }}>
        <h2 className="tab-title">
          Error Log{' '}
          <span className="count-badge">{grandTotal.toLocaleString()} total</span>
          {typeFilter && (
            <span className="count-badge count-badge--yellow">
              {typeFilter}: {typeCounts[typeFilter] ?? 0}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {typeFilter && (
            <button className="ad-btn-sm" onClick={() => setTypeFilter('')}>✕ Clear filter</button>
          )}
          <button className="ad-btn-outline" onClick={() => load(page)}>↺ Refresh</button>
        </div>
      </div>

      {/* ── Pagination (top) ── */}
      {totalPages > 1 && (
        <div className="pagination">
          <button className="ad-btn-sm" disabled={page === 0} onClick={() => setPage(0)}>«</button>
          <button className="ad-btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
          <span className="pagination-info">Page {page + 1} / {totalPages}</span>
          <button className="ad-btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
          <button className="ad-btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
        </div>
      )}

      {loading ? (
        <div className="ad-loading">Loading errors…</div>
      ) : (
        <table className="ad-table ad-table--full">
          <thead>
            <tr>
              <th>Type</th>
              <th>Message</th>
              <th>Path</th>
              <th>IP</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {errors.map((e) => (
              <React.Fragment key={e.id}>
                <tr
                  className={`mod-row${expanded === e.id ? ' mod-row--open' : ''}`}
                  onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                >
                  <td>
                    <span className={`badge badge--${badgeColor(e.error_type)}`}>{e.error_type}</span>
                  </td>
                  <td className="msg-cell">{e.error_message}</td>
                  <td className="path-cell">{e.request_path || <span className="sub-text">—</span>}</td>
                  <td>{e.ip_address ? <code className="ip-code">{e.ip_address}</code> : <span className="sub-text">—</span>}</td>
                  <td>{fmtDate(e.created_at)}</td>
                </tr>
                {expanded === e.id && e.details && Object.keys(e.details).length > 0 && (
                  <tr className="reports-row">
                    <td colSpan={5}>
                      <div className="error-detail">
                        <strong>Details:</strong>
                        <pre className="error-json">{JSON.stringify(e.details, null, 2)}</pre>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {errors.length === 0 && (
              <tr><td colSpan={5} className="empty-cell">No errors logged</td></tr>
            )}
          </tbody>
        </table>
      )}

      {/* ── Pagination (bottom) ── */}
      {totalPages > 1 && (
        <div className="pagination pagination--bottom">
          <button className="ad-btn-sm" disabled={page === 0} onClick={() => setPage(0)}>«</button>
          <button className="ad-btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
          <span className="pagination-info">Page {page + 1} / {totalPages}</span>
          <button className="ad-btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
          <button className="ad-btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export const AdminDashboard: React.FC = () => {
  useAdminGuard();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    adminApi.getStats()
      .then(({ data }) => setStats(data.data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  return (
    <div className="ad-root">
      {/* Sidebar */}
      <nav className="ad-sidebar">
        <div className="ad-brand">
          <span className="ad-brand-icon">⬡</span>
          <div>
            <div className="ad-brand-name">GTA Rental</div>
            <div className="ad-brand-sub">Admin Console</div>
          </div>
        </div>

        <ul className="ad-nav">
          {TABS.map((t) => (
            <li key={t.key}>
              <button
                className={`ad-nav-btn${tab === t.key ? ' active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                <span className="ad-nav-icon">{t.icon}</span>
                {t.label}
                {t.key === 'moderation' && stats && stats.flaggedPins > 0 && (
                  <span className="nav-alert">{stats.flaggedPins}</span>
                )}
              </button>
            </li>
          ))}
        </ul>

        <button className="ad-logout" onClick={() => logout(navigate)}>
          ⏻ Sign Out
        </button>
      </nav>

      {/* Main area */}
      <main className="ad-main">
        <header className="ad-topbar">
          <h1 className="ad-page-title">{TABS.find((t) => t.key === tab)?.label}</h1>
          <div className="ad-topbar-right">
            {statsLoading && <span className="ad-syncing">syncing…</span>}
            <span className="ad-timestamp">
              {new Date().toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
          </div>
        </header>

        {tab === 'overview'   && <OverviewTab stats={stats} />}
        {tab === 'analytics'  && <AnalyticsTab stats={stats} />}
        {tab === 'pins'       && <PinsTab />}
        {tab === 'moderation' && <ModerationTab />}
        {tab === 'ips'        && <IpManagerTab />}
        {tab === 'matching'   && <MatchingTab />}
        {tab === 'errors'     && <ErrorLogTab />}
      </main>
    </div>
  );
};
